import { constants, findEntityById } from "@qgiv/core-js";

/**
 * @public
 * @function prepareValuesForP2PRegistrationSubmit
 * @param {object} settings Object that holds all form settings objects
 * @param {object} classificationValues Object holding classificationValues form values
 * @param {object} paymentValues Object holding payment form values
 * @param {object} discounts Object holding promo values to update registrationTotal
 * @param {Array} promoCodes Array holding all the promo codes applied
 * @param {Array} registrations Array of objects representing participant registrations
 * @param {Array} storeValues Array of objects for each item added to cart
 * @param {string} parentRegistrationId String of parent registration Id or 0 if no parent registration
 * @param {number} shippingMethodAmount Number representing cost of the selected shipping method
 * @param {object} totals Object containing all of the registration totals stored in redux
 * @description Given settings/values/registrations, prepare values for registration
 *              submission to the backend. Translates values to what the backend expects.
 *              Cleans up values object of any values we should not be sending
 *              to the backend.
 * @returns {object} prepared object for registration endpoint
 */
export const prepareValuesForP2PRegistrationSubmit = (
    settings,
    classificationValues,
    paymentValues,
    discounts,
    promoCodes,
    registrations,
    storeValues,
    parentRegistrationId,
    shippingMethodAmount,
    totals,
) => {
    const {
        categories,
        formSettings: { isStandardDonationForm, thirdPartyMatching },
        p2pSettings: {
            allowableEmergencyContacts,
            allowableEmergencyContactsForSub,
            emergencyContactEmail,
            emergencyContactCell,
            enableEmergencyContacts,
            enableClassifications,
            matchingType,
        },
    } = settings;
    const { Classification = [""] } = classificationValues;
    const { participants = [], transaction = {} } = discounts;
    const {
        ENUMS: {
            GiftMatchingService,
            Status: { INACTIVE },
            MatchingType: { MANUAL, AUTOMATIC },
        },
    } = constants;
    const { giftAssistAmount, storeTaxAmount, total } = totals;
    const registrationTotal = total;

    const preparedRegistrations = registrations
        .filter((registration) => Object.keys(registration).length > 0)
        .map((registration, index) => {
            // prevent pass by reference issues when
            // deleting keys from this object
            const newRegistration = {
                ...registration,
            };

            const {
                Category,
                Registration_Role,
                Is_Nfp,
                Participant_Fundraising_Goal,
                Team,
                Team_Captain,
                New_Team_Name,
                New_Team_Fundraising_Goal,
                Existing_Registration = false,
            } = newRegistration;

            // Cleans up the fundraising goal value before delivering it to the the BE
            const getParticipantFundraisingGoalValue = () => {
                const isFundraisingParticipant = !Is_Nfp;
                const participantFundraisingGoal = isFundraisingParticipant
                    ? Participant_Fundraising_Goal
                    : "";

                return {
                    Participant_Fundraising_Goal: participantFundraisingGoal,
                };
            };
            // Cleans up the team values before delivering them to the the BE
            const getTeamValues = () => {
                const hasCreatedANewTeam = Team === "new-team";
                const teamCaptain =
                    hasCreatedANewTeam || Existing_Registration
                        ? Team_Captain
                        : false;
                const newTeamName = hasCreatedANewTeam ? New_Team_Name : "";
                const newTeamFundraisingGoal = hasCreatedANewTeam
                    ? New_Team_Fundraising_Goal
                    : "";

                return {
                    Team_Captain: teamCaptain,
                    New_Team_Name: newTeamName,
                    New_Team_Fundraising_Goal: newTeamFundraisingGoal,
                };
            };
            // BE needs these values passed back in order to validate processing amount
            const getParticipantPromoCodesValues = () => {
                const participantPromo =
                    participants && participants.find(findEntityById(index));
                const originalCategoryFee =
                    participantPromo && participantPromo.originalCategoryFee;
                const postPromoCategoryFee =
                    participantPromo && participantPromo.postPromoCategoryFee;
                const appliedPromos =
                    participantPromo && participantPromo.promoCodes
                        ? participantPromo.promoCodes.map((promo) => {
                              return promo.name;
                          })
                        : [];

                return {
                    Original_Category_Fee: originalCategoryFee,
                    Post_Promo_Category_Fee: postPromoCategoryFee,
                    Promo_Codes: appliedPromos,
                };
            };
            // Structures Emergency Contact values to be passed in the format need by BE
            const getEmergencyContactsValues = () => {
                let emergencyContactsData = [];
                // Determine if Emergency Contacts were displayed for category selected
                const isVisibleByCategory = Category.length
                    ? categories.find(findEntityById(Number(Category[0])))
                          .attachEmergencyContacts
                    : false;

                const isVisible =
                    isVisibleByCategory &&
                    ((index > 0 && allowableEmergencyContactsForSub) ||
                        (index === 0 && enableEmergencyContacts));

                // Structure data based on the display settings in control
                if (isVisible && allowableEmergencyContacts === 2) {
                    emergencyContactsData = [
                        {
                            firstName:
                                newRegistration["Emergency_Contact_First_Name"],
                            lastName:
                                newRegistration["Emergency_Contact_Last_Name"],
                            phone: newRegistration["Emergency_Contact_Phone"],
                        },
                        {
                            firstName:
                                newRegistration[
                                    "Emergency_Contact_First_Name_2"
                                ],
                            lastName:
                                newRegistration[
                                    "Emergency_Contact_Last_Name_2"
                                ],
                            phone: newRegistration["Emergency_Contact_Phone_2"],
                        },
                    ];

                    if (emergencyContactEmail !== INACTIVE) {
                        emergencyContactsData[0].email =
                            newRegistration["Emergency_Contact_Email"];
                        emergencyContactsData[1].email =
                            newRegistration["Emergency_Contact_Email_2"];
                    }
                    if (emergencyContactCell !== INACTIVE) {
                        emergencyContactsData[0].cell =
                            newRegistration["Emergency_Contact_Cell"];
                        emergencyContactsData[1].cell =
                            newRegistration["Emergency_Contact_Cell_2"];
                    }
                } else if (isVisible && allowableEmergencyContacts === 1) {
                    emergencyContactsData = [
                        {
                            firstName:
                                newRegistration["Emergency_Contact_First_Name"],
                            lastName:
                                newRegistration["Emergency_Contact_Last_Name"],
                            phone: newRegistration["Emergency_Contact_Phone"],
                        },
                    ];
                    if (emergencyContactEmail !== INACTIVE) {
                        emergencyContactsData[0].email =
                            newRegistration["Emergency_Contact_Email"];
                    }
                    if (emergencyContactCell !== INACTIVE) {
                        emergencyContactsData[0].cell =
                            newRegistration["Emergency_Contact_Cell"];
                    }
                } else {
                    emergencyContactsData = [];
                }

                // Once new structure is built, delete old values
                delete newRegistration["Emergency_Contact_Cell"];
                delete newRegistration["Emergency_Contact_First_Name"];
                delete newRegistration["Emergency_Contact_Last_Name"];
                delete newRegistration["Emergency_Contact_Phone"];
                delete newRegistration["Emergency_Contact_Email"];
                delete newRegistration["Emergency_Contact_Cell_2"];
                delete newRegistration["Emergency_Contact_First_Name_2"];
                delete newRegistration["Emergency_Contact_Last_Name_2"];
                delete newRegistration["Emergency_Contact_Phone_2"];
                delete newRegistration["Emergency_Contact_Email_2"];

                return {
                    ...newRegistration,
                    Emergency_Contacts: emergencyContactsData,
                };
            };

            // Need to start the formatted registration in a mutated state,
            // since we are calling `delete` on keys of the registration.
            const mutatedRegistration = {
                ...getEmergencyContactsValues(),
            };

            const getClassificationValue = () => {
                if (enableClassifications) {
                    return { Classification: Classification[0] };
                } else {
                    return {};
                }
            };

            return {
                // Spread the mutated registration, then override any values
                // that exist in the mutated registration
                ...mutatedRegistration,
                ...getClassificationValue(),
                Category: Category[0],
                Registration_Role: Registration_Role[0],
                ...getParticipantFundraisingGoalValue(),
                ...getTeamValues(),
                ...getParticipantPromoCodesValues(),
            };
        });

    const getPaymentValuesForRegistrationSubmit = (values) => {
        // Make a fresh copy of values to mutate and send to backend
        let newValues = {
            ...values,
        };

        const getPrivacyOptionsValues = () => {
            const {
                Do_Not_Show_Amount = false,
                Do_Not_Show_Name = false,
                Privacy_Display_Name = true,
                Privacy_Display_Amount = true,
            } = newValues;

            if (isStandardDonationForm) {
                const invertedPrivacyOptions = {
                    Privacy_Display_Amount: !Do_Not_Show_Amount,
                    Privacy_Display_Name: !Do_Not_Show_Name,
                };
                delete newValues["Do_Not_Show_Amount"];
                delete newValues["Do_Not_Show_Name"];
                return { ...newValues, ...invertedPrivacyOptions };
            }
            return {
                ...newValues,
                Privacy_Display_Name,
                Privacy_Display_Amount,
            };
        };

        newValues = getPrivacyOptionsValues();

        const getMatchingGiftValues = () => {
            let matchingData = {};

            // Automatic Matching Gifts
            if (matchingType === AUTOMATIC) {
                if (
                    (values["Matching_Company_Name"] &&
                        values["Matching_Company_Name"].length > 0) ||
                    values["Matching_Donor_Verified"] ||
                    values["Matching_Donor_Found"]
                ) {
                    // Remove the values from the 0th level
                    delete newValues["Matching_Company_Name"];
                    delete newValues["Matching_Company_ID"];
                    delete newValues["Matching_Parent_Raw_ID"];
                    delete newValues["Matching_Contact_Address_Line_1"];
                    delete newValues["Matching_Contact_Address_Line_2"];
                    delete newValues["Matching_Contact_City"];
                    delete newValues["Matching_Contact_State"];
                    delete newValues["Matching_Contact_Zip"];
                    delete newValues["Matching_Contact_Phone"];
                    delete newValues["Matching_Contact_Email"];
                    delete newValues["Matching_Donor_Found"];
                    delete newValues["Matching_Donor_Verified"];
                    delete newValues["Employer"];

                    // Return the prepared value
                    matchingData = {
                        Match_Type: AUTOMATIC,
                        Employer: values.Matching_Company_Name,
                        Company_ID: values.Matching_Company_ID,
                        Parent_Raw_ID: values.Matching_Parent_Raw_ID,
                        Contact_Address_Line_1:
                            values.Matching_Contact_Address_Line_1,
                        Contact_Address_Line_2:
                            values.Matching_Contact_Address_Line_2,
                        Contact_City: values.Matching_Contact_City,
                        Contact_State: values.Matching_Contact_State,
                        Contact_Zip: values.Matching_Contact_Zip,
                        Contact_Phone: values.Matching_Contact_Phone,
                        Contact_Email: values.Matching_Contact_Email,
                        Donor_Found: values.Matching_Donor_Found,
                        Donor_Verified: values.Matching_Donor_Verified,
                    };
                } else if (
                    parseInt(thirdPartyMatching) ===
                    GiftMatchingService.DOUBLE_THE_DONATION
                ) {
                    // Remove the values from the 0th level
                    delete newValues["Matching_Company_Name"];
                    delete newValues["Matching_Company_ID"];
                    delete newValues["Matching_Parent_Raw_ID"];
                    delete newValues["Matching_Contact_Address_Line_1"];
                    delete newValues["Matching_Contact_Address_Line_2"];
                    delete newValues["Matching_Contact_City"];
                    delete newValues["Matching_Contact_State"];
                    delete newValues["Matching_Contact_Zip"];
                    delete newValues["Matching_Contact_Phone"];
                    delete newValues["Matching_Contact_Email"];
                    delete newValues["Matching_Donor_Found"];
                    delete newValues["Matching_Donor_Verified"];

                    // Return blank values for DtD to be treated as a pending unconfirmed match
                    matchingData = {
                        Matching_Type: AUTOMATIC,
                        Employer: "",
                        Company_ID: "",
                    };
                }
            }
            // Manual Matching Gifts
            else if (matchingType === MANUAL) {
                matchingData = {
                    Match_Type: MANUAL,
                    Match_Gift: values.Match_Gift,
                    Employer: values.Employer,
                    Amount: values.Matching_Gift_Amount,
                };
                // Remove the values from the 0th level
                delete newValues["Employer"];
                delete newValues["Match_Gift"];
                delete newValues["Matching_Gift_Amount"];
            }

            return {
                ...newValues,
                Matching_Gift: matchingData,
            };
        };

        const getGiftAssistValue = () => {
            if (values.Gift_Assist && values.Gift_Assist === true) {
                return {
                    Gift_Assist_Amount: giftAssistAmount,
                };
            }
        };

        const getStoreTaxShippingValue = () => {
            if (storeValues && storeValues.length > 0) {
                return {
                    Store_Tax_Amount: storeTaxAmount,
                    Store_Shipping_Method_Amount: shippingMethodAmount,
                };
            }
        };

        // BE needs these values passed back in order to validate processing amount
        const getTransactionPromoCodesValues = () => {
            const originalTransactionFee =
                transaction && transaction.originalCategoryFeeSum;
            const postPromoTransactionFee = transaction && transaction.amount;
            const appliedPromos =
                transaction && transaction.promoCodes
                    ? transaction.promoCodes.map((promo) => {
                          return promo.name;
                      })
                    : [];

            return {
                Original_Transaction_Fee: originalTransactionFee,
                Post_Promo_Transaction_Fee: postPromoTransactionFee,
                Promo_Codes: appliedPromos,
            };
        };
        // Need to start the formatted payment value in a mutated state,
        // since we are calling `delete` on keys of the payment value.
        const mutatedPaymentValue = {
            ...getMatchingGiftValues(),
        };

        return {
            ...mutatedPaymentValue,
            ...getGiftAssistValue(),
            ...getStoreTaxShippingValue(),
            ...getTransactionPromoCodesValues(),
        };
    };

    const getStoreValuesForRegistrationSubmit = (values) => {
        return {
            ...values,
        };
    };

    return {
        paymentValues: {
            ...getPaymentValuesForRegistrationSubmit(paymentValues),
        },
        storeValues: {
            ...getStoreValuesForRegistrationSubmit(storeValues),
        },
        registrations: preparedRegistrations,
        registrationTotal,
        parentRegistrationId,
    };
};
